import type { VisibilityState } from '@tanstack/react-table';
import type { ColumnMapping, TableField, TableFilter } from '../api/types';

export type FilterOption = {
  value: string;
  label: string;
  displayLabel?: string;
  selectedLabel?: string;
  name?: string;
  idValue?: string;
};

export type FilterValueState = {
  inputValue: string;
  displayValue?: string;
  selectedValues: string[];
  selectedLabels?: Record<string, string>;
  items: FilterOption[];
  isFetching: boolean;
  touched?: boolean;
  min?: string;
  max?: string;
  searchMode?: 'contains' | 'startsWith';
};

const DEFAULT_LOOKUP_LABEL_MAX_LENGTH = 52;

export const createDefaultFilterValueState = (): FilterValueState => ({
  inputValue: '',
  displayValue: '',
  selectedValues: [],
  selectedLabels: {},
  items: [],
  isFetching: false,
  touched: false,
  min: '',
  max: '',
});

export const isNumericIdentifier = (value: string): boolean => /^\d+$/.test(value.trim());

type DisplayLabelSource = {
  _id?: string;
  internalName?: string;
  optionalName?: string;
  publicName?: string;
  name?: string;
  category?: unknown;
  pilar?: unknown;
};

const getDisplayLabelAccessor = (item: DisplayLabelSource): string =>
  item.optionalName || item.internalName || item.name || item._id || '';

export const getMappingColumnName = (column: ColumnMapping): string =>
  getDisplayLabelAccessor(column);

const DOCUMENT_TOKENS = ['docum', 'document', 'ident', 'ruc', 'nit', 'dni', 'cedula'];
const SOCIAL_NAME_TOKENS = ['razonsocial', 'razon social', 'social'];
const EXPLICIT_NAME_TOKENS = ['nombre', 'name'];

const normalizeColumnText = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const hasAnyToken = (value: string, tokens: string[]): boolean =>
  tokens.some(token => value.includes(token));

const isNumericLikeValue = (value: unknown): boolean => {
  if (typeof value === 'number') return true;
  if (typeof value !== 'string') return false;
  return isNumericIdentifier(value);
};

const getDisplayLabel = <T extends DisplayLabelSource>(
  item: T,
  items?: T[],
  sampleRow?: Record<string, unknown>
): string => {
  const accessor = getDisplayLabelAccessor(item);
  const baseHeader = item.publicName || item.name || accessor;
  if (!baseHeader) return accessor;

  const normalizedBaseHeader = normalizeColumnText(baseHeader);
  if (
    normalizedBaseHeader.includes('(documento)') ||
    normalizedBaseHeader.includes('(razon social)')
  ) {
    return baseHeader;
  }

  const columnsInGroup = (items ?? []).filter(candidate => {
    const candidateHeader =
      candidate.publicName || candidate.name || getDisplayLabelAccessor(candidate);
    return normalizeColumnText(candidateHeader) === normalizedBaseHeader;
  });

  const hasSiblingColumns = columnsInGroup.length > 1;
  const searchText = normalizeColumnText(
    [
      accessor,
      item.publicName,
      item.name,
      item.internalName,
      item.optionalName,
      typeof item.category === 'string' ? item.category : '',
      typeof item.pilar === 'string' ? item.pilar : '',
    ]
      .filter(Boolean)
      .join(' ')
  );

  const hasSocialToken = hasAnyToken(searchText, SOCIAL_NAME_TOKENS);
  const hasNameToken = hasAnyToken(searchText, EXPLICIT_NAME_TOKENS);
  const hasDocumentToken = hasAnyToken(searchText, DOCUMENT_TOKENS);
  const isSocialNameColumn = hasSocialToken || (hasNameToken && !hasDocumentToken);
  const isExplicitDocumentColumn = hasDocumentToken && !hasSocialToken;
  const groupHasSocialNameColumn = columnsInGroup.some(candidate => {
    const candidateSearchText = normalizeColumnText(
      [
        getMappingColumnName(candidate),
        candidate.publicName,
        candidate.name,
        candidate.internalName,
        candidate.optionalName,
        typeof candidate.category === 'string' ? candidate.category : '',
        typeof candidate.pilar === 'string' ? candidate.pilar : '',
      ]
        .filter(Boolean)
        .join(' ')
    );
    const candidateHasSocialToken = hasAnyToken(candidateSearchText, SOCIAL_NAME_TOKENS);
    const candidateHasNameToken = hasAnyToken(candidateSearchText, EXPLICIT_NAME_TOKENS);
    const candidateHasDocumentToken = hasAnyToken(candidateSearchText, DOCUMENT_TOKENS);
    return candidateHasSocialToken || (candidateHasNameToken && !candidateHasDocumentToken);
  });

  if (hasSiblingColumns && groupHasSocialNameColumn && isSocialNameColumn) {
    return `${baseHeader} (Razon social)`;
  }

  const sampleValue = accessor && sampleRow ? sampleRow[accessor] : undefined;
  const isDocumentColumn =
    isExplicitDocumentColumn ||
    (hasSiblingColumns &&
      groupHasSocialNameColumn &&
      !isSocialNameColumn &&
      isNumericLikeValue(sampleValue));

  if (isDocumentColumn) {
    return `${baseHeader} (Documento)`;
  }

  return baseHeader;
};

export const getDisplayColumnHeader = (
  column: ColumnMapping,
  mapping?: ColumnMapping[],
  sampleRow?: Record<string, unknown>
): string => getDisplayLabel(column, mapping, sampleRow);

export const getDisplayFieldLabel = (
  field: TableField,
  fields?: TableField[],
  sampleRow?: Record<string, unknown>
): string => getDisplayLabel(field, fields, sampleRow);

export const formatLookupSelectionLabel = (
  name: string,
  idValue: string,
  maxLength = DEFAULT_LOOKUP_LABEL_MAX_LENGTH
): string => {
  const normalizedName = name.trim();
  const normalizedId = idValue.trim();
  if (!normalizedName) return normalizedId;
  if (!normalizedId || normalizedName === normalizedId) return normalizedName;

  const safeMaxLength = Math.max(10, maxLength);
  const truncateTo = Math.max(8, Math.ceil(safeMaxLength / 2));
  const truncatedName =
    normalizedName.length > safeMaxLength
      ? `${normalizedName.slice(0, truncateTo).trimEnd()}...`
      : normalizedName;

  return `${truncatedName} · ${normalizedId}`;
};

export const getFilterOptionDisplayLabel = (option: FilterOption): string => {
  return option.displayLabel || option.label || option.value;
};

export const getFilterOptionSelectedLabel = (option: FilterOption): string => {
  return option.selectedLabel || option.displayLabel || option.label || option.value;
};

export const getSelectedFilterLabel = (
  state: FilterValueState | undefined,
  value: string
): string => {
  return state?.selectedLabels?.[value] ?? value;
};

export const isFilterStateActive = (
  filter: TableFilter & { fieldData?: TableField },
  state: FilterValueState | undefined
): boolean => {
  if (!state) return false;
  if (filter.filter === 'date' && filter.values === 'range') {
    return Boolean(state.min) && Boolean(state.max);
  }
  if (filter.multiple) {
    return state.selectedValues.length > 0;
  }
  return Boolean(state.inputValue.trim());
};

export const shouldShowGuidedText = (
  filter: TableFilter & { fieldData?: TableField },
  state: FilterValueState | undefined
): boolean => {
  if (!filter.guidedText) return false;
  if (!state?.touched) return false;
  return !isFilterStateActive(filter, state);
};

export const hydrateFiltersFromHistoric = (
  filtersList: Array<TableFilter & { fieldData?: TableField }>,
  historic: Record<string, unknown>,
  currentValues: Record<string, FilterValueState>
) => {
  const fields = (historic as { fields?: Record<string, unknown> }).fields;
  if (!fields) return currentValues;
  const next = { ...currentValues };

  filtersList.forEach(filter => {
    const fieldValue = fields[filter.field];
    if (fieldValue === undefined || fieldValue === null) return;
    const baseState = next[filter._id] ?? createDefaultFilterValueState();

    if (filter.filter === 'date' && filter.values === 'range') {
      if (typeof fieldValue === 'string') {
        const [min, max] = fieldValue.split('|');
        next[filter._id] = { ...baseState, min: min ?? '', max: max ?? '', touched: false };
      }
      return;
    }

    if (filter.filter === 'date') {
      next[filter._id] = {
        ...baseState,
        inputValue:
          typeof fieldValue === 'string' || typeof fieldValue === 'number'
            ? String(fieldValue)
            : '',
        displayValue:
          typeof fieldValue === 'string' || typeof fieldValue === 'number'
            ? String(fieldValue)
            : '',
        touched: false,
      };
      return;
    }

    if (filter.filter === 'numeric') {
      next[filter._id] = {
        ...baseState,
        inputValue:
          typeof fieldValue === 'string' || typeof fieldValue === 'number'
            ? String(fieldValue)
            : '',
        displayValue:
          typeof fieldValue === 'string' || typeof fieldValue === 'number'
            ? String(fieldValue)
            : '',
        touched: false,
      };
      return;
    }

    if (filter.multiple) {
      const selectedValues = Array.isArray(fieldValue)
        ? fieldValue.map(value =>
            typeof value === 'string' || typeof value === 'number' ? String(value) : ''
          )
        : [
            typeof fieldValue === 'string' || typeof fieldValue === 'number'
              ? String(fieldValue)
              : '',
          ];
      next[filter._id] = {
        ...baseState,
        inputValue: '',
        selectedValues,
        selectedLabels: selectedValues.reduce<Record<string, string>>((acc, value) => {
          acc[value] = value;
          return acc;
        }, {}),
        touched: false,
      };
      return;
    }

    next[filter._id] = {
      ...baseState,
      inputValue:
        typeof fieldValue === 'string' || typeof fieldValue === 'number' ? String(fieldValue) : '',
      displayValue:
        typeof fieldValue === 'string' || typeof fieldValue === 'number' ? String(fieldValue) : '',
      touched: false,
    };
  });

  return next;
};

export const buildFieldsPayload = (
  filtersList: Array<TableFilter & { fieldData?: TableField }>,
  values: Record<string, FilterValueState>
) => {
  const payload: Record<string, unknown> = {};
  filtersList.forEach(filter => {
    const state = values[filter._id];
    if (!state) return;
    if (filter.filter === 'date' && filter.values === 'range') {
      if (state.min && state.max) {
        payload[filter.field] = `${state.min}|${state.max}`;
      }
      return;
    }
    if (filter.filter === 'date' || filter.filter === 'numeric') {
      if (state.inputValue) {
        payload[filter.field] = state.inputValue;
      }
      return;
    }
    if (filter.multiple) {
      if (state.selectedValues.length) {
        payload[filter.field] = state.selectedValues;
      }
      return;
    }
    if (state.inputValue) {
      payload[filter.field] = state.inputValue;
    }
  });
  return payload;
};

export const buildFieldInfo = (
  filtersList: Array<TableFilter & { fieldData?: TableField }>,
  fields: TableField[],
  values: Record<string, FilterValueState>
) => {
  return filtersList
    .map(filter => {
      const state = values[filter._id];
      if (!state) return null;
      const fieldData = fields.find(item => item._id === filter.field);
      const fieldName =
        filter.filter === 'date' ? 'Rango de fecha' : (fieldData?.name ?? filter.field);

      if (filter.filter === 'date' && filter.values === 'range') {
        if (!state.min || !state.max) return null;
        return {
          _id: filter.field,
          fieldName,
          values: `${state.min}|${state.max}`,
        };
      }

      if (filter.multiple && state.selectedValues.length) {
        return {
          _id: filter.field,
          fieldName,
          values: state.selectedValues.map(value => ({
            label: getSelectedFilterLabel(state, String(value)),
            value: String(value),
          })),
        };
      }

      if (state.inputValue) {
        return {
          _id: filter.field,
          fieldName,
          values: [
            {
              label: String(state.displayValue || state.inputValue),
              value: String(state.inputValue),
            },
          ],
        };
      }

      return null;
    })
    .filter(Boolean);
};

export const validateDateRange = (
  filtersList: Array<TableFilter & { fieldData?: TableField }>,
  values: Record<string, FilterValueState>,
  maxDays = 15
) => {
  const hasAdditionalActiveFilter = filtersList.some(filter => {
    const state = values[filter._id];
    if (!state) return false;

    // Ignore date range filters for "additional filter" validation.
    if (filter.filter === 'date' && filter.values === 'range') return false;

    if (filter.multiple) return state.selectedValues.length > 0;
    return Boolean(state.inputValue);
  });

  const dateFilters = filtersList.filter(
    filter => filter.filter === 'date' && filter.values === 'range'
  );

  for (const filter of dateFilters) {
    const state = values[filter._id];
    if (!state?.min || !state?.max) continue;
    const start = Date.parse(state.min);
    const end = Date.parse(state.max);
    if (Number.isNaN(start) || Number.isNaN(end)) continue;
    const diffDays = Math.abs(end - start) / (1000 * 60 * 60 * 24);
    if (diffDays >= maxDays && !hasAdditionalActiveFilter) {
      return `Debes hacer más específica tu búsqueda: Selecciona un filtro adicional o escoge un rango de fecha menor a ${maxDays} dias`;
    }
  }

  return null;
};

const REPORT_FILE_KEYS = ['file', 'url', 'href', 'downloadUrl', 'downloadURL', 'path', 'location'];

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== 'object') return null;
  return value as Record<string, unknown>;
};

const readErrorDetail = (value: unknown): string | null => {
  const record = asRecord(value);
  if (!record) return null;

  if (typeof record.detail === 'string' && record.detail.trim()) {
    return record.detail.trim();
  }

  if (typeof record.message === 'string' && record.message.trim()) {
    return record.message.trim();
  }

  const nestedError = asRecord(record.error);
  if (nestedError) {
    if (typeof nestedError.detail === 'string' && nestedError.detail.trim()) {
      return nestedError.detail.trim();
    }
    const nestedOutput = asRecord(nestedError.output);
    if (nestedOutput && typeof nestedOutput.message === 'string' && nestedOutput.message.trim()) {
      return nestedOutput.message.trim();
    }
  }

  return null;
};

export const extractApiErrorMessage = (error: unknown): string | null => {
  if (error instanceof Error && error.message.trim()) {
    return error.message.trim();
  }

  const directDetail = readErrorDetail(error);
  if (directDetail) return directDetail;

  const record = asRecord(error);
  if (!record) return null;

  return readErrorDetail(record.data);
};

export const isReportGenerationInProgressError = (error: unknown): boolean => {
  const message = extractApiErrorMessage(error);
  if (!message) return false;

  const normalized = message
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  return (
    normalized.includes('operacion de generacion del archivo') ||
    normalized.includes('se encuentra una operacion de generacion')
  );
};

export const extractReportFilePath = (value: unknown, depth = 0): string | null => {
  if (depth > 4) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }

  const record = asRecord(value);
  if (!record) return null;

  for (const key of REPORT_FILE_KEYS) {
    if (!(key in record)) continue;
    const nested = extractReportFilePath(record[key], depth + 1);
    if (nested) return nested;
  }

  for (const key of ['data', 'result', 'output', 'report']) {
    if (!(key in record)) continue;
    const nested = extractReportFilePath(record[key], depth + 1);
    if (nested) return nested;
  }

  return null;
};

export const buildQueryFromFilters = (
  filtersList: Array<TableFilter & { fieldData?: TableField }>,
  activeValues: Record<string, FilterValueState>
) => {
  const validFilters = filtersList.filter(filter => {
    const state = activeValues[filter._id];
    if (!state) return false;
    if (filter.filter === 'date' && filter.values === 'range') {
      return Boolean(state.min) && Boolean(state.max);
    }
    if (filter.filter === 'date') {
      return Boolean(state.inputValue);
    }
    if (filter.filter === 'numeric') {
      return Boolean(state.inputValue);
    }
    if (filter.multiple) {
      return state.selectedValues.length > 0;
    }
    return Boolean(state.inputValue);
  });

  if (!validFilters.length) return undefined;

  return validFilters
    .map(filter => {
      const state = activeValues[filter._id];
      if (!state) return '';
      let value = '';

      if (filter.filter === 'date' && filter.values === 'range') {
        value = `${state.min}|${state.max}`;
      } else if (filter.filter === 'date') {
        value = state.inputValue;
      } else if (filter.filter === 'numeric') {
        value = state.inputValue;
      } else if (filter.multiple) {
        value = state.selectedValues.join('|-|');
      } else {
        value = state.inputValue.replace(/&/g, '<<<>>>');
      }

      const mode = filter.values ?? 'equal';
      return `${filter.field}=${encodeURIComponent(`${mode}~${value}`)}`;
    })
    .filter(Boolean)
    .join('&');
};

/**
 * Country name to ISO 3166-1 alpha-2 code mapping
 * Supports Latin American countries with Spanish and English variants
 */
const COUNTRY_TO_ISO_MAP: Record<string, string> = {
  argentina: 'AR',
  bolivia: 'BO',
  brasil: 'BR',
  brazil: 'BR',
  chile: 'CL',
  colombia: 'CO',
  'costa rica': 'CR',
  cuba: 'CU',
  ecuador: 'EC',
  'el salvador': 'SV',
  guatemala: 'GT',
  honduras: 'HN',
  méxico: 'MX',
  mexico: 'MX',
  nicaragua: 'NI',
  panamá: 'PA',
  panama: 'PA',
  paraguay: 'PY',
  perú: 'PE',
  peru: 'PE',
  'república dominicana': 'DO',
  'dominican republic': 'DO',
  uruguay: 'UY',
  venezuela: 'VE',
  belice: 'BZ',
  belize: 'BZ',
  guyana: 'GY',
  surinam: 'SR',
  suriname: 'SR',
  'guayana francesa': 'GF',
  'french guiana': 'GF',
  general: 'WORLD',
  pais: 'WORLD',
};

/**
 * Normalizes country name for consistent lookup
 * Converts to lowercase, removes accents, and trims whitespace
 */
export const normalizeCountryName = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
};

/**
 * Converts ISO 3166-1 alpha-2 code to flag emoji
 */
export const isoToFlagEmoji = (isoCode: string): string => {
  if (isoCode === 'WORLD') return '🌐';

  const codePoints = isoCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));

  return String.fromCodePoint(...codePoints);
};

/**
 * Returns flag emoji for a country name
 * Falls back to globe emoji for unknown countries
 */
export const getCountryFlag = (countryName: string): string => {
  const normalized = normalizeCountryName(countryName);
  const isoCode = COUNTRY_TO_ISO_MAP[normalized];

  if (!isoCode) {
    return '🌐';
  }

  return isoToFlagEmoji(isoCode);
};

/**
 * Spanish month name to number mapping (1-12)
 */
const MONTH_NAME_TO_NUMBER: Record<string, number> = {
  enero: 1,
  febrero: 2,
  marzo: 3,
  abril: 4,
  mayo: 5,
  junio: 6,
  julio: 7,
  agosto: 8,
  septiembre: 9,
  octubre: 10,
  noviembre: 11,
  diciembre: 12,
};

export type DateRange = {
  minDate: string; // Format: YYYY-MM-DD
  maxDate: string; // Format: YYYY-MM-DD
};

/**
 * Parses a table name to extract date range.
 * Expected format: "Expo Plus (ENERO 2024 - Junio 2025)"
 * Returns the first day of the start month and last day of the end month.
 */
export const parseDateRangeFromTableName = (tableName: string): DateRange | null => {
  // Match pattern like "(ENERO 2024 - Junio 2025)" or "(Enero 2024 - DICIEMBRE 2025)"
  const rangeRegex = /\(([a-záéíóú]+)\s+(\d{4})\s*-\s*([a-záéíóú]+)\s+(\d{4})\)/i;
  const match = tableName.match(rangeRegex);

  if (!match) {
    return null;
  }

  const [, startMonthName, startYearStr, endMonthName, endYearStr] = match;

  const startMonth = MONTH_NAME_TO_NUMBER[startMonthName.toLowerCase()];
  const endMonth = MONTH_NAME_TO_NUMBER[endMonthName.toLowerCase()];
  const startYear = parseInt(startYearStr, 10);
  const endYear = parseInt(endYearStr, 10);

  if (!startMonth || !endMonth || isNaN(startYear) || isNaN(endYear)) {
    return null;
  }

  // Build min date: first day of start month
  const minDate = `${startYear}-${String(startMonth).padStart(2, '0')}-01`;

  // Build max date: last day of end month
  const lastDayOfEndMonth = new Date(endYear, endMonth, 0).getDate();
  const maxDate = `${endYear}-${String(endMonth).padStart(2, '0')}-${String(lastDayOfEndMonth).padStart(2, '0')}`;

  return { minDate, maxDate };
};

/**
 * Creates empty filter values state for resetting filters
 */
export const createEmptyFilterValues = (
  filtersList: Array<TableFilter & { fieldData?: TableField }>
): Record<string, FilterValueState> => {
  const emptyValues: Record<string, FilterValueState> = {};

  filtersList.forEach(filter => {
    emptyValues[filter._id] = createDefaultFilterValueState();
  });

  return emptyValues;
};

/**
 * Applies date range from table name to date filters
 */
export const applyDateRangeToFilters = (
  filtersList: Array<TableFilter & { fieldData?: TableField }>,
  currentValues: Record<string, FilterValueState>,
  dateRange: DateRange
): Record<string, FilterValueState> => {
  const next = { ...currentValues };

  filtersList.forEach(filter => {
    if (filter.filter === 'date' && filter.values === 'range') {
      next[filter._id] = {
        ...(next[filter._id] ?? createDefaultFilterValueState()),
        min: dateRange.minDate,
        max: dateRange.maxDate,
        touched: false,
      };
    }
  });

  return next;
};

export type YearQuickSelect = {
  year: number;
  label: string;
  range: DateRange;
};

/**
 * Parses a date string (YYYY-MM-DD) and returns year, month, day as numbers.
 * Avoids timezone issues with Date constructor.
 */
const parseDateString = (dateStr: string): { year: number; month: number; day: number } | null => {
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  return {
    year: parseInt(match[1], 10),
    month: parseInt(match[2], 10),
    day: parseInt(match[3], 10),
  };
};

/**
 * Generates year quick select options based on the table's date range.
 * Each year button will set the appropriate date range:
 * - First year: from original start month to Dec 31
 * - Middle years: Jan 1 to Dec 31
 * - Last year: Jan 1 to original end month's last day
 * - Single year: restores the full original range
 */
export const generateYearQuickSelects = (tableDateRange: DateRange | null): YearQuickSelect[] => {
  if (!tableDateRange) return [];

  const startParsed = parseDateString(tableDateRange.minDate);
  const endParsed = parseDateString(tableDateRange.maxDate);

  if (!startParsed || !endParsed) return [];

  const startYear = startParsed.year;
  const endYear = endParsed.year;

  const years: YearQuickSelect[] = [];

  for (let year = startYear; year <= endYear; year++) {
    let minDate: string;
    let maxDate: string;

    if (year === startYear && year === endYear) {
      // Single year: use original range
      minDate = tableDateRange.minDate;
      maxDate = tableDateRange.maxDate;
    } else if (year === startYear) {
      // First year: from original start to Dec 31
      minDate = tableDateRange.minDate;
      maxDate = `${year}-12-31`;
    } else if (year === endYear) {
      // Last year: from Jan 1 to original end
      minDate = `${year}-01-01`;
      maxDate = tableDateRange.maxDate;
    } else {
      // Middle year: full year
      minDate = `${year}-01-01`;
      maxDate = `${year}-12-31`;
    }

    years.push({
      year,
      label: String(year),
      range: { minDate, maxDate },
    });
  }

  return years;
};

/**
 * Get default visible columns from fields based on visibility property
 */
export const getDefaultVisibleColumns = (fields: TableField[]): string[] => {
  return fields
    .filter(field => field.visibility === 'visible')
    .map(field => field.name || field.internalName || field._id)
    .filter((name): name is string => Boolean(name));
};

/**
 * Convert column names array to VisibilityState object.
 * Returns empty object (all visible) if visibleColumns is empty.
 */
export const columnsToVisibilityState = (
  allColumns: string[],
  visibleColumns: string[]
): VisibilityState => {
  // If no visible columns specified, return empty object (all columns visible)
  if (!visibleColumns.length) return {};

  const state: VisibilityState = {};
  allColumns.forEach(col => {
    state[col] = visibleColumns.includes(col);
  });
  return state;
};

/**
 * Convert VisibilityState to column names array
 */
export const visibilityStateToColumns = (state: VisibilityState): string[] => {
  return Object.entries(state)
    .filter(([, visible]) => visible)
    .map(([col]) => col);
};

export const fieldIdsToVisibilityState = (
  allColumns: string[],
  mapping: ColumnMapping[],
  fields: TableField[],
  columnFieldIds: string[]
): VisibilityState => {
  if (!columnFieldIds.length) return {};

  const columnNamesByFieldId = new Map<string, string>();

  mapping.forEach(column => {
    if (!column._id) return;
    const columnName = getMappingColumnName(column);
    if (!columnName) return;
    columnNamesByFieldId.set(column._id, columnName);
  });

  fields.forEach(field => {
    const columnName = field.name || field.internalName || field._id;
    if (!columnName) return;
    if (!columnNamesByFieldId.has(field._id)) {
      columnNamesByFieldId.set(field._id, columnName);
    }
  });

  const visibleColumns = columnFieldIds
    .map(fieldId => columnNamesByFieldId.get(fieldId))
    .filter((columnName): columnName is string => Boolean(columnName));

  if (!visibleColumns.length) return {};

  const baseColumns = allColumns.length ? allColumns : Array.from(columnNamesByFieldId.values());
  return columnsToVisibilityState(baseColumns, visibleColumns);
};

/**
 * Daater team ID that has full access to detailed mode
 */
const DAATER_TEAM_ID = '5ba049380cd2a2709ee4c536';

/**
 * Team restrictions type for detailed mode access
 */
export type TeamDetailRestrictions = {
  partidas?: string[];
  sectors?: string[];
};

/**
 * Check if user can see the detailed mode toggle.
 * The toggle is hidden when the table's `isDetailTable` flag is false (e.g. PLUS tables).
 * When the flag is missing the toggle falls back to visible for authenticated users.
 */
export const canAccessDetailedMode = (
  account: Record<string, unknown> | null,
  _teamRestrictions?: TeamDetailRestrictions,
  table?: { isDetailTable?: boolean; name?: string }
): boolean => {
  if (!account) return false;

  // Backend explicitly marks tables that support detail mode
  if (table && table.isDetailTable === false) return false;

  return true;
};

/**
 * Check if user should see demo mode (limited results) when using detailed mode.
 * Demo mode is triggered when team has no restrictions configured.
 */
export const shouldUseDemoMode = (
  account: Record<string, unknown> | null,
  teamRestrictions?: TeamDetailRestrictions
): boolean => {
  // Daater team never uses demo mode
  if (account?.team === DAATER_TEAM_ID) return false;

  // If no restrictions, use demo mode
  if (!teamRestrictions) return true;
  const hasPartidas = (teamRestrictions.partidas?.length ?? 0) > 0;
  const hasSectors = (teamRestrictions.sectors?.length ?? 0) > 0;

  return !hasPartidas && !hasSectors;
};

/**
 * Maximum number of rows shown when demo mode is active.
 */
export const DEMO_ROW_LIMIT = 5;

/**
 * Returns only the first DEMO_ROW_LIMIT records when demoMode is active.
 */
export const limitDemoRecords = <T>(records: T[], demoMode: boolean): T[] =>
  demoMode ? records.slice(0, DEMO_ROW_LIMIT) : records;

/**
 * Separate filters into standard and detail-only groups
 */
export const separateFiltersByDetailMode = (
  filters: Array<TableFilter & { fieldData?: TableField }>
): {
  standardFilters: Array<TableFilter & { fieldData?: TableField }>;
  detailFilters: Array<TableFilter & { fieldData?: TableField }>;
} => {
  const standardFilters = filters.filter(f => !f.isForDetail);
  const detailFilters = filters.filter(f => f.isForDetail);
  return { standardFilters, detailFilters };
};
